import { useMutation } from "@apollo/client/react";
import {
  CircularProgress,
  ClickAwayListener,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  styled,
} from "@mui/material";
import { ArchiveIcon } from "@phosphor-icons/react/dist/csr/Archive";
import { DotsThreeVerticalIcon } from "@phosphor-icons/react/dist/csr/DotsThreeVertical";
import { EyeIcon } from "@phosphor-icons/react/dist/csr/Eye";
import { GitForkIcon } from "@phosphor-icons/react/dist/csr/GitFork";
import { FC, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Permissions from "../../constants/permissions";
import { useAccountContext } from "../../strict/app/AccountResolver";
import { DRAFT_LIST_QUERY } from "../../graphql";
import {
  BranchChangelistDocument,
  BranchChangelistMutation,
  BranchChangelistMutationVariables,
  Changelist,
  SaveChangelistDocument,
  SaveChangelistMutation,
  SaveChangelistMutationVariables,
} from "../../graphql/types";
import DiffIcon from "../../icons/Diff.svg?react";
import { HumanDate } from "../Date";
import { IfPermission } from "../Permissions";
import { DraftIcon, DraftLink, NeedsMerge } from "../ScriptsComponents";
import FlowSummaryIcon from "../common/FlowSummaryIcon";
import { UserAvatarWithTooltip } from "../ui/UserAvatar";

interface DraftRowProps {
  draft: Changelist;
  hasPublished: boolean;
}

const DraftOptionsContainer = styled("div")({
  display: "flex",
  justifyContent: "flex-end",
});

const DraftOptionsMenuItem = styled(MenuItem)(({ theme }) => ({
  "& svg": {
    marginRight: theme.spacing(1),
  },
}));

const createDraftName = (parent?: Changelist) => {
  let name = "New Draft";
  if (parent) {
    name = `Copy of ${parent.description}`;
  }

  return name.slice(0, 500);
};

interface DraftOptionsProps {
  draft: Changelist;
  onArchive: useMutation.MutationFunction<SaveChangelistMutation, SaveChangelistMutationVariables>;
  onClone: useMutation.MutationFunction<BranchChangelistMutation, BranchChangelistMutationVariables>;
  hasPublished?: boolean;
  loading?: boolean;
}

const DraftOptions = ({ draft, onArchive, onClone, hasPublished = false, loading = false }: DraftOptionsProps) => {
  const anchorEl = useRef<HTMLButtonElement>(null);
  const [optionsMenuOpen, setOptionsMenuOpen] = useState(false);

  const navigate = useNavigate();
  const { urlName } = useAccountContext();

  return (
    <DraftOptionsContainer>
      <ClickAwayListener onClickAway={() => setOptionsMenuOpen(false)}>
        {loading ? (
          <CircularProgress size="1em" sx={{ mr: 1.2 }} />
        ) : (
          <IconButton
            ref={anchorEl}
            onClick={() => setOptionsMenuOpen((prev) => !prev)}
            data-testid="draft-options-btn"
          >
            <DotsThreeVerticalIcon />
          </IconButton>
        )}
      </ClickAwayListener>
      <Menu
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        anchorEl={() => anchorEl.current}
        open={optionsMenuOpen}
        data-testid="draft-options-menu"
      >
        <DraftOptionsMenuItem onClick={() => onArchive()} data-testid="archive-draft">
          <ArchiveIcon />
          Archive
        </DraftOptionsMenuItem>
        <DraftOptionsMenuItem
          onClick={() => onClone({ variables: { description: createDraftName(draft), changelist_id: draft.id } })}
          data-testid="clone-draft"
        >
          <GitForkIcon />
          Clone Draft
        </DraftOptionsMenuItem>
        <DraftOptionsMenuItem
          onClick={() => navigate(`/app/${urlName}/scripts/editor/${draft.branched_from}`)}
          data-testid="view-parent"
        >
          <EyeIcon />
          View Parent
        </DraftOptionsMenuItem>
        <IfPermission permission={Permissions.ViewDiffsBeta}>
          {hasPublished ? (
            <DraftOptionsMenuItem
              onClick={() => navigate(`/app/${urlName}/scripts/editor/${draft.id}?mode=Changes`)}
              data-testid="compare-with-published"
            >
              <DiffIcon />
              Compare with Published
            </DraftOptionsMenuItem>
          ) : null}
        </IfPermission>
      </Menu>
    </DraftOptionsContainer>
  );
};

const DraftRow = ({ draft, hasPublished }: DraftRowProps) => {
  const navigate = useNavigate();
  const { urlName } = useAccountContext();

  const [archiveDraft, { loading: archiveDraftLoading }] = useMutation<
    SaveChangelistMutation,
    SaveChangelistMutationVariables
  >(SaveChangelistDocument, {
    variables: { changelist_id: draft.id, action: "archive" },
    onCompleted: () => {
      toast.success("Draft archived");
    },
    onError: () => {
      toast.error("Error archiving draft");
    },
    refetchQueries: ["drafts"],
  });

  const [cloneDraft, { loading: cloneDraftLoading }] = useMutation<
    BranchChangelistMutation,
    BranchChangelistMutationVariables
  >(BranchChangelistDocument, {
    onCompleted: (result) => {
      toast.success("Draft cloned");
      const changelistId = result.branch_changelist?.changelist?.id;
      if (changelistId) {
        navigate(`/app/${urlName}/scripts/editor/${changelistId}`);
      }
    },
    onError: () => {
      toast.error("Error cloning draft");
    },
    refetchQueries: [DRAFT_LIST_QUERY],
  });

  return (
    <TableRow>
      <TableCell>
        <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
          <DraftIcon status={draft.status} />
          <DraftLink description={draft.description} id={draft.id} />
          <FlowSummaryIcon flowSummary={draft.flow_summary} />
          <NeedsMerge required={draft.needs_merge} />
        </Stack>
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <HumanDate date={draft.created_on} />
          {draft.created_by ? (
            <UserAvatarWithTooltip user={draft.created_by} prefix={"Created by "} userDate={draft.created_on} />
          ) : null}
        </Stack>
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <HumanDate date={draft.updated_on} />
          {draft.updated_by ? (
            <UserAvatarWithTooltip user={draft.updated_by} prefix={"Updated by "} userDate={draft.updated_on} />
          ) : null}
        </Stack>
      </TableCell>
      <TableCell>
        <DraftOptions
          draft={draft}
          onArchive={archiveDraft}
          onClone={cloneDraft}
          hasPublished={hasPublished}
          loading={archiveDraftLoading || cloneDraftLoading}
        />
      </TableCell>
    </TableRow>
  );
};

interface IDraftTable {
  drafts: Changelist[];
  hasPublished: boolean;
}

const DraftsTable: FC<IDraftTable> = ({ drafts, hasPublished = false }) => {
  const draftRows = drafts.map((draft, index) => {
    return <DraftRow key={index} draft={draft} hasPublished={hasPublished} />;
  });
  return (
    <TableContainer component={Paper}>
      <Table data-cy="drafts-table" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Description</TableCell>
            <TableCell align="left">Created</TableCell>
            <TableCell align="left">Updated</TableCell>
            <IfPermission permission={Permissions.DevelopScripts}>
              <TableCell align="right">Options</TableCell>
            </IfPermission>
          </TableRow>
        </TableHead>
        <TableBody>{draftRows}</TableBody>
      </Table>
    </TableContainer>
  );
};

export { DraftsTable };
